import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { ListSceneDesignTool } from 'js/src/agent/tools/ListSceneDesignTool';
import { SetCaptionsTool } from 'js/src/agent/tools/SetCaptionsTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';

export const createFivePointListVideoSkill: Skill = {
    name: SKILL_NAMES.createFivePointListVideo,
    description:
        'Listicle-style video built around exactly five numbered points ("5 tips...", "top 5...").',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Listicle video',
        blurb: 'A numbered countdown: "5 tips", "top 5".',
        icon: 'format_list_numbered',
        gradient: ['#5A8A72', '#87996A'],
    },
    get instructions(): string {
        return `# Create 5-Point List Video

The fixed-count listicle format: exactly five numbered points, narrated by an AI
voiceover, each point revealed as its own visual beat.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
5-point formula, and its rules win where they conflict.

## Structure — exactly 5 points, a beat pair each
Each point covers its own words in the transcript with two overlays:
1. **Number overlay** (no media): announces the point — a prominent numeral plus a
   short headline.
2. **Detail overlay** (media): elaborates that same point, illustrated with media
   that concretely shows what the point is about.
- Never skip the Number overlay, never merge Number and Detail into one overlay,
  and never let their word ranges bleed into each other. A long Detail can split
  across overlays at natural clause breaks.
- If the source content doesn't give exactly 5 points (the user supplied 3, or 8),
  work with them to trim/combine/expand to 5, or confirm they want a different
  count — the fixed count IS the format's identity; don't quietly ship another.

## Numbering
- Every Number overlay shows a prominent numeral 1-5 — required, not optional. The
  numeral is structural chrome, exempt from the spoken-words rule: set it with its
  own ${SetTextTool.toolName} call on the number asset; it needn't be a spoken word.
- Any headline text beyond the bare numeral follows the normal spoken-words rule.
- Numbers read 1 through 5 in order, no skips or repeats, one consistent style
  throughout (don't mix "3" and "Point 3").

## Design selection — filter, then pick
A missing number usually traces to picking a design before checking what it holds.
Filter using the actual asset list ${ListSceneDesignTool.toolName} returns, not names:
1. Number overlays: only designs with a clear, prominent numeral slot.
2. Then by media: Number overlay designs have no media asset; Detail overlay
   designs have one.
3. Rotate through the survivors across the 5 points — vary Number designs and
   Detail designs separately, no repeats.
4. If nothing satisfies both filters, flag it — don't drop the number or force
   media onto a Number overlay. Never a caption-style design for either beat.

## Script structure
- Target roughly 60-90 seconds of narration — the fixed count is the identity, not
  the running time; keep each point's explanation concise rather than padding.
- Use the user's 5 points as-is when given; otherwise offer to draft them and
  confirm before generating.
- Open with a hook line naming the list's topic (the title card, on a "title"
  design); write the narration point by point — headline then explanation, for all
  5; close with a real outro — recap plus a topic-specific follow line on an
  "outro" design.
- **Captions are OFF for this format** — on-screen text (including the numbers)
  does that job. Call ${SetCaptionsTool.toolName} with enabled: false right after generating the
  voiceover.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- All 5 points present: own Number overlay then own Detail overlay, nothing
  skipped, merged, or duplicated.
- Every Number overlay visibly shows its numeral.
- Tally the media/no-media split overlay by overlay; confirm 1-5 in order with a
  consistent style; confirm both design rotations vary; confirm the title-card
  open, outro close, and captions off.`;
    },
};
